import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiMail, FiCheck, FiChevronRight, FiChevronLeft, FiShare2, FiEye, FiEyeOff, FiLoader, FiLock } from 'react-icons/fi';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import CountryPhoneInput from '../components/CountryPhoneInput';
import { validatePhoneForCountry, parsePhoneWithCountryCode } from '../utils/phoneValidation';
import { usersApi, organizationsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

const initialFormState = {
  username: '',
  email: '',
  firstName: '',
  lastName: '',
  password: '',
  confirmPassword: '',
  organization: '',
  department: '',
  manager: '',
  title: '',
  role: '',
  roleId: '',
  status: 'Pending',
  enabled: true,
  totp: false,
  // Which owner the user belongs to: the id from CustomerAccount/Vendor plus the table it
  // came from, so the id is never ambiguous between the two.
  trustCenterOrganizationId: '',
  trustCenterOwnerType: '',
  // Work Contact
  workPhone: '',
  workPhoneCountry: 'US',
  workAddress1: '',
  workAddress2: '',
  workCity: '',
  workState: '',
  workPostalCode: '',
  workCountryCode: '',
  // Personal Contact
  personalPhone: '',
  personalPhoneCountry: 'US',
  personalAddress1: '',
  personalAddress2: '',
  personalState: '',
  personalPostalCode: '',
  personalCountryCode: '',
  // Additional Attributes
  location: '',
  timezone: '',
  industry: '',
  focusArea: '',
  discipline: ''
};

const steps = [
  { id: 0, title: 'Basic Info', description: 'Account details' },
  { id: 1, title: 'Organization', description: 'Work information' },
  { id: 2, title: 'Work Contact', description: 'Work address' },
  { id: 3, title: 'Personal Contact', description: 'Personal address' },
  { id: 4, title: 'Attributes', description: 'Additional info' }
];

const Users = () => {
  const { user: currentUser } = useAuth();
  const currentRoles = Array.isArray(currentUser?.roles) ? currentUser.roles : [];
  const isAdmin = currentRoles.includes('admin') &&
    !currentRoles.includes('assessmentadmin') &&
    !currentRoles.includes('departmentadmin') &&
    !currentRoles.includes('department_admin');
  const isAssessmentAdminScoped = !isAdmin && currentRoles.includes('assessmentadmin');
  const isDeptAdmin =
    currentRoles.includes('department_admin') ||
    currentRoles.includes('departmentadmin') ||
    currentUser?.role === 'department_admin' || currentUser?.role === 'departmentadmin';
  // Scoped admin = can only see/manage users of their own role
  const isScopedAdmin = !isAdmin && (isAssessmentAdminScoped || isDeptAdmin);
  // The Keycloak role name a scoped admin is allowed to assign/see
  const scopedRoleName = isAssessmentAdminScoped
    ? 'assessmentadmin'
    : isDeptAdmin
      ? (currentRoles.includes('departmentadmin') ? 'departmentadmin' : 'department_admin')
      : '';
  const currentUserDept = currentUser?.department || '';
  const currentUserOrg = currentUser?.organization || '';

  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ totalUsers: 0, activeUsers: 0, pendingUsers: 0, adminCount: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  // True when the user picked "Other" for Organization and is typing a custom value.
  const [orgIsCustom, setOrgIsCustom] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [validationErrors, setValidationErrors] = useState({});

  const [submitting, setSubmitting] = useState(false);

  // Password management state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Trust Center Organizations
  const [tcOrganizations, setTcOrganizations] = useState([]);

  // Owners a user can belong to: organizations (customer accounts) AND business entities
  // (vendors), as { ownerType, ownerId, name }.
  const [owners, setOwners] = useState([]);

  // Available Keycloak roles
  const [availableRoles, setAvailableRoles] = useState([]);

  // The owner a scoped admin is confined to. Preferably the one captured on their own profile;
  // otherwise resolved from their organization name.
  const scopedOwner = useMemo(() => {
    if (!isScopedAdmin) return null;
    const capturedId = currentUser?.trustCenterOrganizationId;
    const capturedType = currentUser?.trustCenterOwnerType;
    if (capturedId) {
      const type = (capturedType || 'customeraccount').toLowerCase();
      const match = owners.find(o => String(o.ownerId) === String(capturedId) && o.ownerType === type);
      if (match) return match;
    }
    const orgLc = currentUserOrg.toLowerCase().trim();
    if (!orgLc) return null;
    // Organizations win over business entities when a name exists in both.
    return owners.find(o => o.ownerType === 'customeraccount' && (o.name || '').toLowerCase().trim() === orgLc)
      || owners.find(o => (o.name || '').toLowerCase().trim() === orgLc)
      || null;
  }, [isScopedAdmin, owners, currentUserOrg, currentUser?.trustCenterOrganizationId, currentUser?.trustCenterOwnerType]);

  // Fetch all users from API once
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersResponse, statsResponse, orgsResponse, rolesResponse, ownersResponse] = await Promise.all([
        usersApi.getAll(1, 10000),
        usersApi.getStats(),
        organizationsApi.getAll(1, 100),
        usersApi.getAllRoles(),
        usersApi.getOwners()
      ]);
      setUsers(usersResponse.data || []);
      setStats(statsResponse);
      setTcOrganizations(orgsResponse?.data || []);
      setAvailableRoles(rolesResponse || []);
      setOwners(Array.isArray(ownersResponse) ? ownersResponse : []);
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // A scoped admin's members are whatever the user service resolves for their owner — profiles
  // carrying that owner, users attached through business-entity relationships, and legacy
  // name matches. Enterprise admins skip this and keep the full list.
  const [ownerUsers, setOwnerUsers] = useState(null);
  const fetchOwnerUsers = useCallback(async () => {
    if (!isScopedAdmin || !scopedOwner) {
      setOwnerUsers(null);
      return;
    }
    try {
      const result = await usersApi.getByOwner(scopedOwner.ownerType, scopedOwner.ownerId);
      setOwnerUsers(Array.isArray(result) ? result : []);
    } catch (err) {
      console.error('Failed to load users for', scopedOwner, err);
      setOwnerUsers(null);
    }
  }, [isScopedAdmin, scopedOwner]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchOwnerUsers();
  }, [fetchOwnerUsers]);

  // Scoped admins (assessmentadmin / departmentadmin) only see users from their own owner.
  // Enterprise admin sees all users. Until the owner-scoped list arrives — or if it failed —
  // fall back to the plain `organization` attribute match rather than showing everyone.
  const visibleUsers = useMemo(() => {
    if (!isScopedAdmin) return users;
    if (ownerUsers) return ownerUsers;
    const orgLc = currentUserOrg.toLowerCase().trim();
    if (!orgLc) return [];
    return users.filter(u => (u.organization || '').toLowerCase().trim() === orgLc);
  }, [users, isScopedAdmin, currentUserOrg, ownerUsers]);

  const visibleRoles = useMemo(() => {
    if (!isScopedAdmin) return availableRoles;
    if (isAssessmentAdminScoped) {
      const excluded = ['admin', 'assessmentadmin', 'departmentadmin', 'department_admin'];
      return availableRoles.filter(r => !excluded.includes((r.name || '').toLowerCase()));
    }
    if (isDeptAdmin) {
      const excluded = ['admin', 'assessmentadmin', 'departmentadmin', 'department_admin'];
      return availableRoles.filter(r => !excluded.includes((r.name || '').toLowerCase()));
    }
    return availableRoles;
  }, [availableRoles, isScopedAdmin, isAssessmentAdminScoped, isDeptAdmin]);

  // Helper to resolve TC org name
  const getTcOrgName = useCallback(
    (user) => tcOrganizations.find(o => String(o.id) === String(user.trustCenterOrganizationId))?.name || '',
    [tcOrganizations]
  );

  // Column definitions for DataTable
  const columns = useMemo(() => [
    {
      key: 'user',
      header: 'User',
      sortable: true,
      filterable: true,
      accessor: (row) => `${row.firstName || ''} ${row.lastName || ''} ${row.email || ''}`,
      render: (row) => (
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-[#0077b5] text-white flex items-center justify-center text-xs font-semibold flex-shrink-0">
            {row.firstName?.charAt(0) || ''}{row.lastName?.charAt(0) || ''}
          </div>
          <div>
            <p className="text-sm font-medium text-[#53545a]">{row.firstName} {row.lastName}</p>
            <p className="text-xs text-[#919191]">{row.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'organization',
      header: 'Organization',
      sortable: true,
      filterable: true,
      accessor: (row) => row.organization || '',
      render: (row) => <span className="text-sm text-[#919191]">{row.organization || '-'}</span>,
    },
    // {
    //   key: 'tcOrganization',
    //   header: 'TC Organization',
    //   sortable: true,
    //   filterable: true,
    //   accessor: (row) => getTcOrgName(row),
    //   render: (row) => <span className="text-sm text-[#919191]">{getTcOrgName(row) || '-'}</span>,
    // },
    {
      key: 'department',
      header: 'Department',
      sortable: true,
      filterable: true,
      accessor: (row) => row.department || '',
      render: (row) => <span className="text-sm text-[#919191]">{row.department || '-'}</span>,
    },
    {
      key: 'title',
      header: 'Title',
      sortable: true,
      filterable: true,
      accessor: (row) => row.title || '',
      render: (row) => <span className="text-sm text-[#919191]">{row.title || '-'}</span>,
    },
    {
      key: 'role',
      header: 'Role',
      sortable: true,
      filterable: true,
      accessor: (row) => row.role || '',
      render: (row) => (
        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getRoleBadge(row.role)}`}>{row.role}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      filterable: true,
      accessor: (row) => row.status || '',
      render: (row) => (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadge(row.status)}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${row.status === 'Active' ? 'bg-[#0077b5]' : row.status === 'Pending' ? 'bg-[#F47F2E]' : 'bg-[#919191]'}`}></span>
          {row.status}
        </span>
      ),
    },
  ], [getTcOrgName]);

  // Action menu items per row
  const getActions = useCallback((user) => [
    
    { label: 'Edit', icon: FiEdit2, onClick: () => openEditModal(user) },
  
    { divider: true },
    { label: 'Delete', icon: FiTrash2, onClick: () => openDeleteModal(user), danger: true },
  ], []);

  const openCreateModal = () => {
    const scopedRoleObj = isScopedAdmin && scopedRoleName
      ? availableRoles.find(r => (r.name || '').toLowerCase() === scopedRoleName.toLowerCase())
      : null;
    setFormData({
      ...initialFormState,
      // Scoped admins are locked to their own org and role
      ...(isScopedAdmin && currentUserOrg && { organization: currentUserOrg }),
      // …and new users they create inherit their owner, so membership is captured up front
      // instead of relying on the organization name matching later.
      ...(isScopedAdmin && scopedOwner && {
        organization: scopedOwner.name,
        trustCenterOwnerType: scopedOwner.ownerType,
        trustCenterOrganizationId: String(scopedOwner.ownerId),
      }),
      ...(scopedRoleObj && {
        roleId: scopedRoleObj.id,
        role: scopedRoleObj.name,
      }),
    });
    setOrgIsCustom(false);
    setIsEditing(false);
    setCurrentStep(0);
    setValidationErrors({});
    setShowPassword(false);
    setShowConfirmPassword(false);
    setSubmitting(false);
    setIsModalOpen(true);
  };

  const openEditModal = async (user) => {
    // Fetch user's current Keycloak roles
    let userRoleId = '';
    let userRoleName = user.role || 'User';
    try {
      const userRoles = await usersApi.getUserRoles(user.id);
      if (userRoles && userRoles.length > 0) {
        userRoleId = userRoles[0].id || '';
        userRoleName = userRoles[0].name || user.role || 'User';
      }
    } catch (err) {
      console.error('Failed to fetch user roles:', err);
    }

    setFormData({
      username: user.username || '',
      email: user.email || '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      password: '', // Never populate password for editing
      confirmPassword: '',
      organization: user.organization || '',
      department: user.department || '',
      manager: user.manager || '',
      title: user.title || '',
      role: userRoleName,
      roleId: userRoleId,
      status: user.status || 'Pending',
      enabled: user.enabled ?? true,
      totp: user.totp ?? false,
      // Carry the captured owner through the edit so saving doesn't drop it. A user with an
      // id but no type predates the type being recorded and was always a customer account.
      trustCenterOrganizationId: user.trustCenterOrganizationId || '',
      trustCenterOwnerType: user.trustCenterOwnerType
        || (user.trustCenterOrganizationId ? 'customeraccount' : ''),
      // Work Contact
      workPhone: user.workPhone || '',
      workPhoneCountry: user.workPhoneCountry || parsePhoneWithCountryCode(user.workPhone).countryCode || 'US',
      workAddress1: user.workAddress1 || '',
      workAddress2: user.workAddress2 || '',
      workCity: user.workCity || '',
      workState: user.workState || '',
      workPostalCode: user.workPostalCode || '',
      workCountryCode: user.workCountryCode || '',
      // Personal Contact
      personalPhone: user.personalPhone || '',
      personalPhoneCountry: user.personalPhoneCountry || parsePhoneWithCountryCode(user.personalPhone).countryCode || 'US',
      personalAddress1: user.personalAddress1 || '',
      personalAddress2: user.personalAddress2 || '',
      personalState: user.personalState || '',
      personalPostalCode: user.personalPostalCode || '',
      personalCountryCode: user.personalCountryCode || '',
      // Additional Attributes
      location: user.location || '',
      timezone: user.timezone || '',
      industry: user.industry || '',
      focusArea: user.focusArea || '',
      discipline: user.discipline || ''
    });
    // Reset the explicit "Other" flag; whether the saved org is custom is derived at render
    // time (see step 1 / Organization), which is reliable because the org list is loaded by
    // then. Computing it here can be wrong if tcOrganizations hasn't loaded yet.
    setOrgIsCustom(false);
    setSelectedUser(user);
    setIsEditing(true);
    setCurrentStep(0);
    setValidationErrors({});
    setSubmitting(false);
    setIsModalOpen(true);
  };

  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePhoneChange = (name, fullValue, countryCode) => {
    const countryFieldName = name === 'workPhone' ? 'workPhoneCountry' : 'personalPhoneCountry';
    setFormData(prev => ({
      ...prev,
      [name]: fullValue,
      [countryFieldName]: countryCode
    }));
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateStep = (step) => {
    const errors = {};
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    switch (step) {
      case 0: // Basic Info
        if (!formData.username?.trim()) errors.username = 'Username is required';
        else if (/\s/.test(formData.username)) errors.username = 'Username cannot contain spaces';
        if (!formData.email?.trim()) {
          errors.email = 'Email is required';
        } else if (!emailPattern.test(formData.email)) {
          errors.email = 'Enter a valid email address';
        }
        if (!formData.firstName?.trim()) errors.firstName = 'First name is required';
        if (!formData.lastName?.trim()) errors.lastName = 'Last name is required';
        if (!formData.roleId) errors.roleId = 'Role is required';
        if (!formData.status) errors.status = 'Status is required';
        
        // Password validation
        if (!isEditing) {
          // Create mode: password is required
          if (!formData.password?.trim()) {
            errors.password = 'Password is required';
          } else if (formData.password.length < 8) {
            errors.password = 'Password must be at least 8 characters';
          } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
            errors.password = 'Password must contain uppercase, lowercase, and number';
          }
          if (!formData.confirmPassword?.trim()) {
            errors.confirmPassword = 'Confirm password is required';
          } else if (formData.password !== formData.confirmPassword) {
            errors.confirmPassword = 'Passwords do not match';
          }
        } else {
          // Edit mode: password is optional, but if provided, both fields required and must match
          if (formData.password?.trim() || formData.confirmPassword?.trim()) {
            if (!formData.password?.trim()) {
              errors.password = 'Password is required when changing password';
            } else if (formData.password.length < 8) {
              errors.password = 'Password must be at least 8 characters';
            } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
              errors.password = 'Password must contain uppercase, lowercase, and number';
            }
            if (!formData.confirmPassword?.trim()) {
              errors.confirmPassword = 'Confirm password is required';
            } else if (formData.password !== formData.confirmPassword) {
              errors.confirmPassword = 'Passwords do not match';
            }
          }
        }
        break;

      case 1: // Organization
        if (!formData.organization?.trim()) errors.organization = 'Organization is required';
        if (!formData.department) errors.department = 'Department is required';
        if (!formData.title?.trim()) errors.title = 'Title is required';
        break;

      case 2: // Work Contact
        if (!formData.workPhone?.trim()) {
          errors.workPhone = 'Work phone is required';
        } else {
          const result = validatePhoneForCountry(formData.workPhone, formData.workPhoneCountry);
          if (!result.valid) errors.workPhone = result.message;
        }
        if (!formData.workAddress1?.trim()) errors.workAddress1 = 'Work address is required';
        if (!formData.workCity?.trim()) errors.workCity = 'City is required';
        if (!formData.workState?.trim()) errors.workState = 'State is required';
        if (!formData.workPostalCode?.trim()) errors.workPostalCode = 'Postal code is required';
        if (!formData.workCountryCode?.trim()) errors.workCountryCode = 'Country is required';
        break;

      case 3: // Personal Contact
        if (formData.personalPhone?.trim()) {
          const result = validatePhoneForCountry(formData.personalPhone, formData.personalPhoneCountry);
          if (!result.valid) errors.personalPhone = result.message;
        }
        if (formData.personalPostalCode?.trim() && !/^[A-Za-z0-9\s-]{3,10}$/.test(formData.personalPostalCode.trim())) {
          errors.personalPostalCode = 'Enter a valid postal code';
        }
        break;

      case 4: // Additional Attributes
        if (!formData.location?.trim()) errors.location = 'Location is required';
        if (!formData.timezone) errors.timezone = 'Timezone is required';
        break;

      default:
        break;
    }
    return errors;
  };

  const handleNext = () => {
    const errors = validateStep(currentStep);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors({});
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    setValidationErrors({});
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const errors = validateStep(currentStep);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setSubmitting(false);
      return;
    }
    setValidationErrors({});
    try {
      // Find the selected role object from availableRoles
      const selectedRole = availableRoles.find(r => r.id === formData.roleId);
      const rolePayload = selectedRole ? [{ id: selectedRole.id, name: selectedRole.name, description: selectedRole.description || '' }] : [];

      if (isEditing) {
        await usersApi.update(selectedUser.id, formData);

        // Assign role if selected
        if (rolePayload.length > 0) {
          try {
            await usersApi.assignRoles(selectedUser.id, rolePayload);
          } catch (roleErr) {
            console.error('Failed to assign role:', roleErr);
            alert(`User updated but failed to assign role: ${roleErr.message}`);
          }
        }

        // Set password if provided in edit mode
        if (formData.password?.trim()) {
          try {
            await usersApi.setPassword(selectedUser.id, formData.password);
          } catch (pwdErr) {
            console.error('Failed to set password:', pwdErr);
            alert(`User updated but failed to set password: ${pwdErr.message}`);
          }
        }
      } else {
        // Create the user first
        const createdUser = await usersApi.create(formData);

        if (createdUser && createdUser.id) {
          // Assign role
          if (rolePayload.length > 0) {
            try {
              await usersApi.assignRoles(createdUser.id, rolePayload);
            } catch (roleErr) {
              console.error('Failed to assign role:', roleErr);
              alert(`User created but failed to assign role: ${roleErr.message}`);
            }
          }

          // Set password (required for create)
          if (formData.password) {
            try {
              await usersApi.setPassword(createdUser.id, formData.password);
            } catch (pwdErr) {
              console.error('Failed to set password:', pwdErr);
              alert(`User created but failed to set password: ${pwdErr.message}`);
            }
          }
        }
      }
      setIsModalOpen(false);
      setCurrentStep(0);
      setShowPassword(false);
      setShowConfirmPassword(false);
      fetchUsers();
    } catch (err) {
      console.error('Failed to save user:', err);
      alert('Failed to save user: ' + err.message);
    }
    setSubmitting(false);
  };

  const handleDelete = async () => {
    try {
      await usersApi.delete(selectedUser.id);
      setIsDeleteModalOpen(false);
      fetchUsers();
    } catch (err) {
      console.error('Failed to delete user:', err);
      alert('Failed to delete user: ' + err.message);
    }
  };

  // Export handlers
  const handleExportCSV = async () => {
    try {
      const blob = await usersApi.exportCSV('');
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('CSV export failed:', error);
      alert('Failed to export CSV. Please try again.');
    }
  };

  const handleExportExcel = async () => {
    try {
      const blob = await usersApi.exportExcel('');
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `users_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Excel export failed:', error);
      alert('Failed to export Excel. Please try again.');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'Active': 'bg-[#0077b5]/20 text-[#0077b5]',
      'Pending': 'bg-[#919191]/20 text-[#919191]',
      'Inactive': 'bg-[#919191]/20 text-[#53545a]'
    };
    return styles[status] || styles['Pending'];
  };

  const getRoleBadge = (role) => {
    const lowerRole = (role || '').toLowerCase();
    // Admin-type roles
    if (['admin', 'assessmentadmin', 'tprmteam', 'itpn'].includes(lowerRole))
      return 'bg-[#F47F2E]/20 text-[#F47F2E]';
    // Reviewer-type roles
    if (['reviewer', 'sme', 'riskofficer', 'bro', 'ciso', 'auditor'].includes(lowerRole))
      return 'bg-[#0077b5]/20 text-[#0077b5]';
    // Approver
    if (lowerRole === 'approver')
      return 'bg-[#6366f1]/20 text-[#6366f1]';
    // Trust center user
    if (lowerRole === 'trustcenteruser')
      return 'bg-[#10b981]/20 text-[#10b981]';
    // UMA protection
    if (lowerRole === 'uma_protection')
      return 'bg-[#f59e0b]/20 text-[#f59e0b]';
    // Default
    return 'bg-[#919191]/20 text-[#53545a]';
  };

  // Input styling
  const labelClass = `block text-sm font-medium mb-2 text-[#53545a]`;
  const getInputClass = (fieldName) => `w-full px-4 py-2.5 border text-sm focus:outline-none focus:ring-2 ${validationErrors[fieldName] ? 'border-[#F47F2E] focus:ring-[#F47F2E]' : 'border-[#919191]/30 focus:ring-[#0077b5]'} bg-white text-[#53545a]`;
  const getSelectClass = (fieldName) => `w-full px-4 py-2.5 border text-sm focus:outline-none focus:ring-2 ${validationErrors[fieldName] ? 'border-[#F47F2E] focus:ring-[#F47F2E]' : 'border-[#919191]/30 focus:ring-[#0077b5]'} bg-white text-[#53545a]`;
  const renderFieldError = (fieldName) => validationErrors[fieldName] ? <p className="text-[#F47F2E] text-xs mt-1">{validationErrors[fieldName]}</p> : null;

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Basic Info
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className={labelClass}>Username *</label>
              <input type="text" name="username" value={formData.username} onChange={e => { e.target.value = e.target.value.replace(/\s/g, ''); handleInputChange(e); }} className={getInputClass('username')} placeholder="Enter username (no spaces)" />
              {renderFieldError('username')}
            </div>
            <div>
              <label className={labelClass}>Email *</label>
              <input type="email" name="email" value={formData.email} onChange={handleInputChange} className={getInputClass('email')} placeholder="email@company.com" />
              {renderFieldError('email')}
            </div>
            <div>
              <label className={labelClass}>First Name *</label>
              <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} className={getInputClass('firstName')} placeholder="Enter first name" />
              {renderFieldError('firstName')}
            </div>
            <div>
              <label className={labelClass}>Last Name *</label>
              <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} className={getInputClass('lastName')} placeholder="Enter last name" />
              {renderFieldError('lastName')}
            </div>
            
            {/* Password Section */}
            <div>
              <label className={labelClass}>Password {!isEditing && '*'}</label>
              <div className="relative">
                <input 
                  type={showPassword ? "text" : "password"} 
                  name="password" 
                  value={formData.password} 
                  onChange={handleInputChange} 
                  className={getInputClass('password')} 
                  placeholder={isEditing ? "Leave blank to keep current password" : "Enter password (min 8 characters)"} 
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#919191] hover:text-[#53545a]"
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              {renderFieldError('password')}
              <p className="text-xs text-[#919191] mt-1">
                {isEditing ? 'Optional - leave blank to keep current password' : 'Must contain uppercase, lowercase, and number'}
              </p>
            </div>
            <div>
              <label className={labelClass}>Confirm Password {!isEditing && '*'}</label>
              <div className="relative">
                <input 
                  type={showConfirmPassword ? "text" : "password"} 
                  name="confirmPassword" 
                  value={formData.confirmPassword} 
                  onChange={handleInputChange} 
                  className={getInputClass('confirmPassword')} 
                  placeholder="Confirm password" 
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#919191] hover:text-[#53545a]"
                >
                  {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              {renderFieldError('confirmPassword')}
            </div>
            
            <div>
              <label className={labelClass}>Role *</label>
              {isScopedAdmin && isEditing && selectedUser?.id === currentUser?.id ? (
                <input
                  type="text"
                  value={formData.role}
                  readOnly
                  className={`${getInputClass('roleId')} bg-[#919191]/10 cursor-not-allowed`}
                />
              ) : (
                <select name="roleId" value={formData.roleId} onChange={(e) => {
                  const selectedId = e.target.value;
                  const selectedRole = availableRoles.find(r => r.id === selectedId);
                  setFormData(prev => ({
                    ...prev,
                    roleId: selectedId,
                    role: selectedRole?.name || ''
                  }));
                  if (validationErrors.roleId) {
                    setValidationErrors(prev => ({ ...prev, roleId: '' }));
                  }
                }} className={getSelectClass('roleId')}>
                  <option value="">Choose Role</option>
                  {/* Always include the edited user's existing role even if filtered from visibleRoles */}
                  {isEditing && formData.roleId && !visibleRoles.some(r => r.id === formData.roleId) && (
                    <option key={formData.roleId} value={formData.roleId}>{formData.role}</option>
                  )}
                  {visibleRoles.map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              )}
              {renderFieldError('roleId')}
            </div>
            <div>
              <label className={labelClass}>Status *</label>
              <select name="status" value={formData.status} onChange={handleInputChange} className={getSelectClass('status')}>
                <option value="Pending">Pending</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
              {renderFieldError('status')}
            </div>
            <div className="flex items-center gap-4 col-span-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" name="enabled" checked={formData.enabled} onChange={handleInputChange} className="w-4 h-4 text-[#0077b5] rounded focus:ring-[#0077b5]" />
                <span className={`text-sm text-[#53545a]`}>Account Enabled</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" name="totp" checked={formData.totp} onChange={handleInputChange} className="w-4 h-4 text-[#0077b5] rounded focus:ring-[#0077b5]" />
                <span className={`text-sm text-[#53545a]`}>Enforce 2FA (TOTP)</span>
              </label>
            </div>
            
          </div>
        );

      case 1: // Organization
      {
        // The picker offers both kinds of owner — organizations (customer accounts) and
        // business entities (vendors) — keyed by "<type>:<id>" so the two id spaces can't
        // collide. `organization` still holds the display name for the Keycloak attribute.
        const orgLc = (formData.organization || '').trim().toLowerCase();
        const selectedOwner = formData.trustCenterOrganizationId && formData.trustCenterOwnerType
          ? owners.find(o => String(o.ownerId) === String(formData.trustCenterOrganizationId)
              && o.ownerType === formData.trustCenterOwnerType)
          : owners.find(o => (o.name || '').trim().toLowerCase() === orgLc);
        const ownerKey = selectedOwner ? `${selectedOwner.ownerType}:${selectedOwner.ownerId}` : '';
        const organizationOwners = owners.filter(o => o.ownerType === 'customeraccount');
        const vendorOwners = owners.filter(o => o.ownerType === 'vendor');

        // Show the custom-org textbox when the user explicitly picked "Other", OR when the
        // saved organization matches no known owner (derived here, where `owners` is reliably
        // loaded — so an existing org like "ZenCo" stays selected in the dropdown).
        // Scoped admins have their org locked — never show the custom-org text box for them.
        const showCustom = !isScopedAdmin && (orgIsCustom || (!!orgLc && !selectedOwner));

        // Department field, reused in different grid positions depending on whether the
        // custom-organization textbox is taking the top-right cell.
        const departmentField = (
          <div>
            <label className={labelClass}>Department *</label>
            <select name="department" value={formData.department} onChange={handleInputChange} className={getSelectClass('department')}>
              <option value="">Select department</option>
              <option value="IT">IT</option>
              <option value="HR">HR</option>
              <option value="Finance">Finance</option>
              <option value="Operations">Operations</option>
              <option value="Security">Security</option>
              <option value="Engineering">Engineering</option>
              <option value="Compliance">Compliance</option>
              <option value="Legal">Legal</option>
              <option value="Sales">Sales</option>
              <option value="Marketing">Marketing</option>
            </select>
            {renderFieldError('department')}
          </div>
        );

        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Organization — locked for scoped admins, free dropdown for enterprise admin */}
            <div>
              <label className={labelClass}>Organization *</label>
              {isScopedAdmin ? (
                <input
                  type="text"
                  value={formData.organization}
                  readOnly
                  className={`${getInputClass('organization')} bg-[#919191]/10 cursor-not-allowed`}
                />
              ) : (
                <select
                  name="organizationSelect"
                  value={showCustom ? '__other__' : ownerKey}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '__other__') {
                      // A hand-typed organization has no row to point at, so clear the owner.
                      setOrgIsCustom(true);
                      setFormData(prev => ({
                        ...prev,
                        organization: '',
                        trustCenterOwnerType: '',
                        trustCenterOrganizationId: '',
                      }));
                    } else {
                      setOrgIsCustom(false);
                      const picked = owners.find(o => `${o.ownerType}:${o.ownerId}` === val);
                      setFormData(prev => ({
                        ...prev,
                        organization: picked ? picked.name : '',
                        trustCenterOwnerType: picked ? picked.ownerType : '',
                        trustCenterOrganizationId: picked ? String(picked.ownerId) : '',
                      }));
                    }
                    if (validationErrors.organization) {
                      setValidationErrors(prev => ({ ...prev, organization: '' }));
                    }
                  }}
                  className={getSelectClass('organization')}
                >
                  <option value="">Select organization</option>
                  {organizationOwners.length > 0 && (
                    <optgroup label="Organizations">
                      {organizationOwners.map(o => (
                        <option key={`customeraccount:${o.ownerId}`} value={`customeraccount:${o.ownerId}`}>{o.name}</option>
                      ))}
                    </optgroup>
                  )}
                  {vendorOwners.length > 0 && (
                    <optgroup label="Business Entities">
                      {vendorOwners.map(o => (
                        <option key={`vendor:${o.ownerId}`} value={`vendor:${o.ownerId}`}>{o.name}</option>
                      ))}
                    </optgroup>
                  )}
                  <option value="__other__">Other (enter manually)</option>
                </select>
              )}
              {renderFieldError('organization')}
            </div>

            {/* Top-right cell: the custom-org textbox when "Other" is chosen, otherwise Department */}
            {showCustom ? (
              <div>
                <label className={labelClass}>Organization Name *</label>
                <input
                  type="text"
                  name="organization"
                  value={formData.organization}
                  onChange={handleInputChange}
                  autoComplete="off"
                  placeholder="Enter organization name"
                  className={getInputClass('organization')}
                />
                {renderFieldError('organization')}
              </div>
            ) : (
              departmentField
            )}

            {/* Title */}
            <div>
              <label className={labelClass}>Title *</label>
              <input type="text" name="title" value={formData.title} onChange={handleInputChange} className={getInputClass('title')} placeholder="Enter job title" />
              {renderFieldError('title')}
            </div>

            {/* When "Other" took the top-right cell, Department drops to this row beside Title */}
            {showCustom && departmentField}
          </div>
        );
      }

      case 2: // Work Contact
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <CountryPhoneInput
                name="workPhone"
                value={formData.workPhone}
                countryCodeValue={formData.workPhoneCountry}
                onChange={handlePhoneChange}
                error={validationErrors.workPhone}
                required
                label="Work Phone *"
              />
            </div>
            <div className="md:col-span-2">
              <label className={labelClass}>Work Address Line 1 *</label>
              <input type="text" name="workAddress1" value={formData.workAddress1} onChange={handleInputChange} className={getInputClass('workAddress1')} placeholder="Enter street address" />
              {renderFieldError('workAddress1')}
            </div>
            <div className="md:col-span-2">
              <label className={labelClass}>Work Address Line 2</label>
              <input type="text" name="workAddress2" value={formData.workAddress2} onChange={handleInputChange} className={getInputClass('workAddress2')} placeholder="Apt, suite, unit, etc." />
            </div>
            <div>
              <label className={labelClass}>City *</label>
              <input type="text" name="workCity" value={formData.workCity} onChange={handleInputChange} className={getInputClass('workCity')} placeholder="Enter city" />
              {renderFieldError('workCity')}
            </div>
            <div>
              <label className={labelClass}>State / Province *</label>
              <input type="text" name="workState" value={formData.workState} onChange={handleInputChange} className={getInputClass('workState')} placeholder="Enter state" />
              {renderFieldError('workState')}
            </div>
            <div>
              <label className={labelClass}>Postal Code *</label>
              <input type="text" name="workPostalCode" value={formData.workPostalCode} onChange={handleInputChange} className={getInputClass('workPostalCode')} placeholder="Enter postal code" />
              {renderFieldError('workPostalCode')}
            </div>
            <div>
              <label className={labelClass}>Country *</label>
              <input type="text" name="workCountryCode" value={formData.workCountryCode} onChange={handleInputChange} className={getInputClass('workCountryCode')} placeholder="Enter country" />
              {renderFieldError('workCountryCode')}
            </div>
          </div>
        );

      case 3: // Personal Contact
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <CountryPhoneInput
                name="personalPhone"
                value={formData.personalPhone}
                countryCodeValue={formData.personalPhoneCountry}
                onChange={handlePhoneChange}
                error={validationErrors.personalPhone}
                label="Personal Phone"
              />
            </div>
            <div className="md:col-span-2">
              <label className={labelClass}>Personal Address Line 1</label>
              <input type="text" name="personalAddress1" value={formData.personalAddress1} onChange={handleInputChange} className={getInputClass('personalAddress1')} placeholder="Enter street address" />
              {renderFieldError('personalAddress1')}
            </div>
            <div className="md:col-span-2">
              <label className={labelClass}>Personal Address Line 2</label>
              <input type="text" name="personalAddress2" value={formData.personalAddress2} onChange={handleInputChange} className={getInputClass('personalAddress2')} placeholder="Apt, suite, unit, etc." />
            </div>
            <div>
              <label className={labelClass}>State / Province</label>
              <input type="text" name="personalState" value={formData.personalState} onChange={handleInputChange} className={getInputClass('personalState')} placeholder="Enter state" />
              {renderFieldError('personalState')}
            </div>
            <div>
              <label className={labelClass}>Postal Code</label>
              <input type="text" name="personalPostalCode" value={formData.personalPostalCode} onChange={handleInputChange} className={getInputClass('personalPostalCode')} placeholder="Enter postal code" />
              {renderFieldError('personalPostalCode')}
            </div>
            <div>
              <label className={labelClass}>Country</label>
              <input type="text" name="personalCountryCode" value={formData.personalCountryCode} onChange={handleInputChange} className={getInputClass('personalCountryCode')} placeholder="Enter country" />
              {renderFieldError('personalCountryCode')}
            </div>
          </div>
        );

      case 4: // Additional Attributes
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className={labelClass}>Location *</label>
              <input type="text" name="location" value={formData.location} onChange={handleInputChange} className={getInputClass('location')} placeholder="e.g., San Francisco, CA" />
              {renderFieldError('location')}
            </div>
            <div>
              <label className={labelClass}>Timezone *</label>
              <select name="timezone" value={formData.timezone} onChange={handleInputChange} className={getSelectClass('timezone')}>
                <option value="">Select timezone</option>
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="America/Anchorage">Alaska Time (AKT)</option>
                <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
                <option value="Europe/London">London (GMT)</option>
                <option value="Europe/Paris">Central European (CET)</option>
                <option value="Asia/Tokyo">Japan (JST)</option>
                <option value="Asia/Shanghai">China (CST)</option>
                <option value="Asia/Kolkata">India (IST)</option>
                <option value="Australia/Sydney">Sydney (AEST)</option>
              </select>
              {renderFieldError('timezone')}
            </div>
            <div>
              <label className={labelClass}>Industry</label>
              <input type="text" name="industry" value={formData.industry} onChange={handleInputChange} className={getInputClass('industry')} placeholder="e.g., Technology, Finance" />
            </div>
            <div>
              <label className={labelClass}>Focus Area</label>
              <input type="text" name="focusArea" value={formData.focusArea} onChange={handleInputChange} className={getInputClass('focusArea')} placeholder="e.g., Cloud Security, Compliance" />
            </div>
            <div className="md:col-span-2">
              <label className={labelClass}>Discipline</label>
              <input type="text" name="discipline" value={formData.discipline} onChange={handleInputChange} className={getInputClass('discipline')} placeholder="e.g., Engineering, Risk Management" />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-bold text-[#53545a]">Add or edit a user</h1>
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-[#F47F2E] text-white">
          {visibleUsers.length} users
        </span>
      </div>

      {/* Role-based subtitle */}
      {!loading && (
        <p className="text-sm text-[#919191]">
          {isAdmin
            ? 'You can view all users across all organizations and create new ones from here.'
            : 'You can view and create users within your organization.'}
        </p>
      )}

      {/* Page-level skeleton (header/stats) while loading */}
      {loading && (
        <div className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="w-80 h-9 bg-[#e6eef5] rounded animate-pulse mb-2" />
             
            </div>
            <div className="w-40 h-10 bg-[#e6eef5] rounded animate-pulse" />
          </div>

         
        </div>
      )}

      <DataTable
        columns={columns}
        data={visibleUsers}
        rowKey="id"
        loading={loading}
        error={error}
        onRetry={fetchUsers}
        searchable
        searchPlaceholder="Search users..."
        actions={getActions}
        headerActions={
          <button
            onClick={openCreateModal}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#F47F2E] text-white text-sm font-medium hover:bg-[#e06a1f] transition-colors shadow-sm"
          >
            <FiPlus className="text-base" /> Add User
          </button>
        }
        totalLabel="users"
        emptyMessage="No users found"
      />

      {/* Create/Edit Modal with Step Wizard */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setSubmitting(false); }}
        title={isEditing ? "Edit User" : "Add New User"}
        size="xl"
        footer={
          <div className="flex justify-between w-full">
            <div>
              {currentStep > 0 && (
                <button
                  onClick={handlePrevious}
                  className={`inline-flex items-center gap-2 px-4 py-2 border text-sm transition-colors border-[#919191]/30 text-[#919191] hover:bg-[#919191]/5`}
                >
                  <FiChevronLeft /> Previous
                </button>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setIsModalOpen(false); setCurrentStep(0); setSubmitting(false); }}
                className={`px-4 py-2 border text-sm transition-colors border-[#919191]/30 text-[#919191] hover:bg-[#919191]/5`}
              >
                Cancel
              </button>
              {currentStep < steps.length - 1 ? (
                <button
                  onClick={handleNext}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#F47F2E] text-white text-sm font-medium hover:bg-[#e06a1f] transition-colors"
                >
                  Next <FiChevronRight />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#F47F2E] text-white text-sm font-medium hover:bg-[#e06a1f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting && <FiLoader className="animate-spin" />}
                  {submitting ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create User')}
                </button>
              )}
            </div>
          </div>
        }
      >
        {/* Step Indicator */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    index < currentStep 
                      ? 'bg-[#0077b5] text-white' 
                      : index === currentStep 
                        ? 'bg-[#F47F2E] text-white' 
                        : 'bg-[#919191]/20 text-[#919191]'
                  }`}>
                    {index < currentStep ? <FiCheck /> : index + 1}
                  </div>
                  <div className="mt-2 text-center hidden sm:block">
                    <p className={`text-xs font-medium text-[#53545a]`}>{step.title}</p>
                    <p className={`text-xs text-[#919191]`}>{step.description}</p>
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-12 sm:w-20 h-0.5 mx-2 ${
                    index < currentStep 
                      ? 'bg-[#0077b5]' 
                      : 'bg-[#919191]/20'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className={`p-4 rounded-lg bg-[#919191]/5`}>
          {renderStepContent()}
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete User"
        size="sm"
        footer={
          <>
            <button
              onClick={() => setIsDeleteModalOpen(false)}
              className={`px-4 py-2 border rounded-lg text-sm transition-colors border-[#919191]/30 text-[#919191] hover:bg-[#919191]/5`}
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="px-4 py-2 bg-[#F47F2E] text-white rounded-lg text-sm font-medium hover:bg-[#e06a1f] transition-colors"
            >
              Delete User
            </button>
          </>
        }
      >
        <div className="text-center py-4">
          <div className="w-16 h-16 bg-[#F47F2E]/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiTrash2 className="text-[#F47F2E] text-2xl" />
          </div>
          <h3 className={`text-lg font-medium mb-2 text-[#53545a]`}>Are you sure?</h3>
          <p className="text-[#919191]">
            You are about to delete <span className={`font-medium text-[#53545a]`}>{selectedUser?.firstName} {selectedUser?.lastName}</span>. 
            This action cannot be undone.
          </p>
        </div>
      </Modal>

    </div>
  );
};

export default Users;
